<template>
  <div class="top-bar">
    <!-- Desktop: show buttons -->
    <div class="top-bar-buttons" style="padding-bottom:30px;">
      <a class="top-account-button button" @click="showAccountDialog=true">Account</a>
      <a href="/" class="top-logout-button button" @click.prevent="logout" :class="{ 'loading': isLogoutLoading }">
        <span v-if="!isLogoutLoading">Logout</span>
        <span v-else class="spinner"></span>
      </a>
    </div>
    <!-- Mobile: show hamburger -->
    <div class="hamburger-menu-container" style="padding-bottom:20px;">
      <button class="hamburger-btn" @click="showMenu = !showMenu" aria-label="Open menu">
        <span class="hamburger-icon"></span>
      </button>
      <div v-if="showMenu" class="dropdown-menu" @click.self="showMenu = false">
        <button class="dropdown-item" @click="showMenu = false; showAccountDialog = true">Account</button>
        <button class="dropdown-item" @click="showMenu = false; logout()" :disabled="isLogoutLoading">
          <span v-if="!isLogoutLoading">Logout</span>
          <span v-else class="spinner"></span>
        </button>
      </div>
    </div>
  </div>
  <div>
    <!--Logout and Account buttons-->

    <template v-if="isLoading">
      <div class="initial-loading-spinner" style="display: flex; justify-content: center; align-items: center; min-height: 300px;">
        <div class="spinner" style="margin-right: 12px;"></div>
        <span>Loading your account...</span>
      </div>
    </template>
    <div v-else>
      <h1 class="moments-title">Collection</h1>
      <div class="add-and-view-messages-section">
        <!-- Add message -->
        <form class="add-message-form" @submit.prevent="addMessage">
          <div class="form-group" :class="{ 'message-input-error': messageError }">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0px;">
              <label for="new-moment" class="message-label">Hey, it's me...</label>
              <router-link
                to="/tips-for-success"
                class="message-text"
                style="margin-bottom: 2px; color: #7da47e; text-decoration: none;"
              >
              Feeling stuck?
              </router-link>
            </div>
            <textarea
              id="new-moment"
              v-model="newMessage"
              :maxlength="maxLength"
              rows="4"
              placeholder="(type a new message for yourself here)"
              @input="updateCharCount"
            ></textarea>
            <div class="char-count" :class="{ 'limit-reached': newMessage.length >= maxLength }">
              {{ newMessage.length }} / {{ maxLength }}
            </div>
          </div>
          <button type="submit" :disabled="isAddingMessage">
            <span v-if="!isAddingMessage">Add</span>
            <span v-else class="spinner"></span>
          </button>
        </form>
        <!-- List of messages -->
        <div v-if="isMessagesLoading" class="loading-messages">
          <div class="spinner"></div>
          <span>Loading your messages...</span>
        </div>
        <div v-else-if="messages.length" class="messages-list">
          <div v-for="(message, idx) in messages" :key="message.id" class="message-item">
            <div v-if="editIndex !== idx" class="message-row" @click="handleMessageClick(idx)">
              <div class="message-content">
                <span class="message-text">{{ message.text }}</span>
                <div class="message-date">{{ formatMessageDate(message.createdDate) }}</div>
              </div>
              <div class="message-actions desktop-only">
                <button class="edit-btn" @click.stop="startEdit(idx)">Edit</button>
                <button class="delete-btn" @click.stop="deleteMessage(idx)" aria-label="Delete" :disabled="isDeletingMessage">
                  <span v-if="!isDeletingMessage">
                    <svg class="trash-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M7.5 8.5V14.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/><path d="M12.5 8.5V14.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/><path d="M3.5 5.5H16.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/><path d="M5.5 5.5V16.5C5.5 17.0523 5.94772 17.5 6.5 17.5H13.5C14.0523 17.5 14.5 17.0523 14.5 16.5V5.5" stroke="white" stroke-width="1.5"/><path d="M8.5 2.5H11.5C12.0523 2.5 12.5 2.94772 12.5 3.5V5.5H7.5V3.5C7.5 2.94772 7.94772 2.5 8.5 2.5Z" stroke="white" stroke-width="1.5"/></svg>
                  </span>
                  <span v-else class="spinner"></span>
                </button>
              </div>
            </div>
            <div v-else class="edit-section">
              <textarea
                v-model="editMessage"
                :maxlength="maxLength"
                rows="3"
                @input="updateEditCharCount"
              ></textarea>
              <div class="message-date edit-date">{{ formatMessageDate(messages[idx].createdDate) }}</div>
              <div class="char-count" :class="{ 'limit-reached': editMessage.length >= maxLength }">
                {{ editMessage.length }} / {{ maxLength }}
              </div>
              <div class="edit-actions">
                <button class="cancel-btn" @click="cancelEdit">Cancel</button>
                <button class="delete-btn mobile-delete" @click="deleteMessage(idx)" aria-label="Delete" :disabled="isDeletingMessage">
                  <span v-if="!isDeletingMessage">Delete</span>
                  <span v-else class="spinner"></span>
                </button>
                <button class="save-btn" @click="saveEdit(idx)" :disabled="!editMessage.trim() || editMessage.length > maxLength || isSavingEdit">
                  <span v-if="!isSavingEdit">Save</span>
                  <span v-else class="spinner"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-messages">
          <span v-if="!isMessagesLoading">No messages yet.</span>
        </div>
      </div>
    </div>

    <AccountEditDialog
      :show="showAccountDialog"
      :account="accountForm"
      :original-email="originalEmail"
      :messages="messages"
      @close="showAccountDialog = false"
      @account-updated="onAccountUpdated"
      @account-deleted="onAccountDeleted"
      @account-changed="loadAccountInfo"
    />
    
    <!-- Popup Dialog Component -->
    <PopupDialog
      :show="popup.show"
      :type="popup.type"
      :title="popup.title"
      :message="popup.message"
      :primary-button-text="popup.primaryButtonText"
      :cancel-button-text="popup.cancelButtonText"
      :show-cancel="popup.showCancel"
      :is-loading="popup.isLoading"
      @close="closePopup"
      @confirm="handlePopupConfirm"
    />
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi';
import PopupDialog from '@/components/shared/PopupDialog.vue';
import AccountEditDialog from './AccountEditDialog.vue';

export default {
  name: 'AccountPage',
  components: {
    PopupDialog,
    AccountEditDialog
  },
  data() {
    return {
      newMessage: '',
      messageError: false,
      messages: [], // Will be loaded from API
      maxLength: 750,
      editIndex: null,
      editMessage: '',
      showAccountDialog: false,
      showDeleteAccountDialog: false,
      showDeleteConfirmationDialog: false,
      showVerificationDialog: false,
      showCodeSentMessage: false,
      showEmailVerifiedMessage: false,
      showAccountUpdatedMessage: false,
      showAccountDeletedMessage: false,
      showEmailVerificationRequiredMessage: false,
      showAccountUpdateAttemptsExceededDialog: false,
      isEmailVerified: true,
      emailChanged: false,
      originalEmail: '',
      verificationAttempts: 0, // Track verification code attempts
      verificationError: '', // Error message for verification dialog
      accountForm: {
        name: '',
        email: '',
        subscriptionStatus: 'Active',
        pricingPlan: 'LegacyFreeEmail',
      },
      verificationCode: '',
      isLoading: true,
      // Loading states for various operations
      isLogoutLoading: false,
      isAccountSaving: false,
      isRequestingCode: false,
      isValidatingCode: false,
      isMessagesLoading: true,
      isAddingMessage: false,
      isSavingEdit: false,
      isDeletingMessage: false,
      // Popup dialog state
      popup: {
        show: false,
        type: 'info',
        title: '',
        message: '',
        primaryButtonText: 'OK',
        cancelButtonText: 'Cancel',
        showCancel: false,
        isLoading: false,
        onConfirm: null
      },
      showMenu: false
    };
  },
  setup() {
    const api = useApi();
    return {
      api
    };
  },
  watch: {
    newMessage(val) {
      if (val.trim()) this.messageError = false;
    }
  },
  mounted() {
      Promise.all([this.loadAccountInfo(), this.loadMessages()]).finally(() => {
        this.isLoading = false;
        const paymentResult = this.$route.query.payment;
        if (paymentResult === 'success') {
          this.$router.replace({ query: {} });
          this.showPopup('success', 'One more step!', 'Check your texts and reply YES to the message we just sent you to complete SMS setup.', {
            primaryButtonText: 'OK',
            onConfirm: () => {
              this.closePopup();
              this.showAccountDialog = true;
            }
          });
        } else if (paymentResult === 'cancelled') {
          this.$router.replace({ query: {} });
          this.showAccountDialog = true;
        } else if (this.$route.query.edit === 'true') {
          this.showAccountDialog = true;
        }
      });
  },
  methods: {
    // Client-side input sanitization
    sanitizeInput(input) {
      if (!input) return '';
      
      // Remove potential XSS characters and trim
      let sanitized = input.trim()
        .replace(/[<>"/\\]/g, '') // Remove potentially dangerous characters
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/vbscript:/gi, '') // Remove vbscript: protocol
        .replace(/on\w+=/gi, ''); // Remove event handlers
      
      return sanitized;
    },
    // Format date for message display
    formatMessageDate(date) {
      if (!date) return '';
      const options = { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      return date.toLocaleDateString('en-US', options);
    },
    // Popup dialog helper methods
    showPopup(type, title, message, options = {}) {
      this.popup = {
        show: true,
        type,
        title,
        message,
        primaryButtonText: options.primaryButtonText || 'OK',
        cancelButtonText: options.cancelButtonText || 'Cancel',
        showCancel: options.showCancel || false,
        isLoading: false,
        onConfirm: options.onConfirm || null
      };
    },
    closePopup() {
      this.popup.show = false;
      this.popup.onConfirm = null;
    },
    handlePopupConfirm() {
      if (this.popup.onConfirm) {
        this.popup.onConfirm();
      } else {
        this.closePopup();
      }
    },
    async loadAccountInfo() {
      try {
        console.log('Starting loadAccountInfo...');
        // No need to check for token in localStorage; rely on httpOnly cookie
        console.log('Making request to get account info...');
        const accountData = await this.api.account.getAccount();
        console.log('Account data received:', accountData);
        // Update account form data, including accountFree
        this.accountForm.name = accountData.name || '';
        this.accountForm.email = accountData.email || '';
        this.accountForm.msgFrequency = accountData.msgFrequency || '1-2'; // Default frequency if not set
        this.accountForm.accountId = accountData.accountId !== undefined ? accountData.accountId : '';
        this.accountForm.accountFree = typeof accountData.accountFree !== 'undefined' ? accountData.accountFree : false;
        this.accountForm.selfMessageDeliveryMethod = accountData.selfMessageDeliveryMethod || 'Email';
        this.accountForm.phoneNumber = accountData.phoneNumber || '';
        this.accountForm.subscriptionStatus = accountData.subscriptionStatus || 'Active';
        this.accountForm.pricingPlan = accountData.pricingPlan || 'LegacyFreeEmail';
        // Store original email for comparison
        this.originalEmail = accountData.email || '';
        console.log('loadAccountInfo completed successfully');
      } catch (error) {
        console.error('Error loading account info:', error);
        // Handle authentication errors
        if (error.message.includes('401') || error.message.toLowerCase().includes('unauthorized')) {
          this.$router.push('/');
        }
        // Don't redirect on other network errors, just log them
      }
    },
    async loadMessages() {
      try {
        console.log('Starting loadMessages...');
        console.log('Making request to get messages...');
        const messagesData = await this.api.moments.getMoments();
        console.log('Messages data received:', messagesData);
        // Transform API data to match our component format
        this.messages = messagesData.map(moment => ({
          id: moment.momentId,
          text: moment.momentDetails,
          createdDate: new Date(moment.momentCreatedDate)
        }))
        // Sort messages from newest to oldest
        .sort((a, b) => b.createdDate - a.createdDate);
        console.log('Messages loaded successfully:', this.messages.length, 'messages');
      } catch (error) {
        console.error('Error loading messages:', error);
        if (error.message && (error.message.includes('401') || error.message.toLowerCase().includes('unauthorized'))) {
          this.$router.push('/');
        }
      } finally {
            if (this.isMessagesLoading) {
              this.isMessagesLoading = false;
            }
      }
    },
    async addMessage() {
      if (!this.newMessage.trim() || this.newMessage.length > this.maxLength) {
        this.messageError = true;
        return;
      }
      if (this.isAddingMessage) return;
      
      this.isAddingMessage = true;
      try {
        // No need to check for token in localStorage
        // Sanitize the input before sending
        const messageText = this.sanitizeInput(this.newMessage);
        if (!messageText || messageText.length === 0) {
          this.showPopup('warning', 'Invalid Input', 'Please enter valid message content.');
          return;
        }
        
        console.log('Creating message:', messageText);

        const newMessageData = await this.api.moments.create(messageText);
        console.log('Message created successfully:', newMessageData);
        console.log('Response data type:', typeof newMessageData);
        console.log('Response keys:', Object.keys(newMessageData || {}));
        
        // Add the new message to the beginning of the list
        this.messages.unshift({
          id: newMessageData.momentId, // Use the correct backend ID
          text: messageText,
          createdDate: newMessageData.momentCreatedDate ? new Date(newMessageData.momentCreatedDate) : new Date()
        });
        
        // Clear the input
        this.newMessage = '';
        console.log('Message added to local list');
      } catch (error) {
        console.error('Error creating message:', error);
        if (error.isSessionExpired) {
          this.showPopup('warning', 'Session Ended', 'Your session ended, please log back in.', {
            onConfirm: () => this.$router.push({ path: '/', query: { login: 'required', redirect: '/account' } })
          });
        } else {
          this.showPopup('error', 'Save Failed', 'Failed to save message. Please try again.');
        }
      } finally {
        this.isAddingMessage = false;
      }
    },
    async deleteMessage(idx) {
      if (this.isDeletingMessage) return; // Prevent double-clicks
      
      const message = this.messages[idx];
      if (!message) return;
      
      this.isDeletingMessage = true;
      try {
        // No need to check for token in localStorage

        // Ensure momentId is sent as an integer
        const momentId = parseInt(message.id);
        console.log('Deleting message:', momentId);

        await this.api.moments.deleteMoment(momentId);
        console.log('Message deleted successfully');

        // Remove from local list
        this.messages.splice(idx, 1);
        
        // Cancel edit if we're editing this message
        if (this.editIndex === idx) this.cancelEdit();
        // Adjust editIndex if it's after the deleted item
        else if (this.editIndex > idx) this.editIndex--;
      } catch (error) {
        console.error('Error deleting message:', error);
        if (error.isSessionExpired) {
          this.showPopup('warning', 'Session Ended', 'Your session ended, please log back in.', {
            onConfirm: () => this.$router.push({ path: '/', query: { login: 'required', redirect: '/account' } })
          });
        } else {
          this.showPopup('error', 'Delete Failed', 'Failed to delete message. Please try again.');
        }
      } finally {
        this.isDeletingMessage = false;
      }
    },
    startEdit(idx) {
      this.editIndex = idx;
      this.editMessage = this.messages[idx].text;
    },
    handleMessageClick(idx) {
      // Make messages clickable on all devices
      this.startEdit(idx);
    },
    async saveEdit(idx) {
      if (!this.editMessage.trim() || this.editMessage.length > this.maxLength || this.isSavingEdit) return;
      
      const message = this.messages[idx];
      if (!message) return;
      
      this.isSavingEdit = true;
      try {
        // No need to check for token in localStorage

        const updatedText = this.sanitizeInput(this.editMessage);
        if (!updatedText || updatedText.length === 0) {
          this.showPopup('warning', 'Invalid Input', 'Please enter valid message content.');
          return;
        }

        // Ensure momentId is sent as an integer
        const momentId = parseInt(message.id);
        console.log('Updating message:', momentId, 'with text:', updatedText);

        await this.api.moments.update(momentId, updatedText);
        console.log('Message updated successfully');

        // Update the local message
        this.messages[idx] = {
          ...this.messages[idx],
          text: updatedText,
        };
        
        // Cancel edit mode
        this.cancelEdit();
      } catch (error) {
        console.error('Error updating message:', error);
        if (error.isSessionExpired) {
          this.showPopup('warning', 'Session Ended', 'Your session ended, please log back in.', {
            onConfirm: () => this.$router.push({ path: '/', query: { login: 'required', redirect: '/account' } })
          });
        } else {
          this.showPopup('error', 'Update Failed', 'Failed to update message. Please try again.');
        }
      } finally {
        this.isSavingEdit = false;
      }
    },
    cancelEdit() {
      this.editIndex = null;
      this.editMessage = '';
    },
    updateCharCount() {
      if (this.newMessage.length > this.maxLength) {
        this.newMessage = this.newMessage.slice(0, this.maxLength);
      }
    },
    updateEditCharCount() {
      if (this.editMessage.length > this.maxLength) {
        this.editMessage = this.editMessage.slice(0, this.maxLength);
      }
    },
    async logout() {
      if (this.isLogoutLoading) return;
      this.isLogoutLoading = true;
      try {
        await this.api.account.logout();
        this.$router.push('/');
      } catch (error) {
        this.$router.push('/');
      } finally {
        this.isLogoutLoading = false;
      }
    },
    onAccountUpdated(updatedAccount) {
      this.accountForm = { ...updatedAccount };
      this.loadAccountInfo();
    },
    onAccountDeleted() {
      this.$router.push('/');
    }
  },
};
</script>

<style scoped>
.add-and-view-messages-section {
  max-width: 800px;
  margin: 8px auto 0 auto;
  padding: 5px 16px 24px 16px;
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  text-align: left;
  box-sizing: border-box;
  flex: 1 0 auto;
}
@media (max-width: 800px) {
  .add-and-view-messages-section {
    max-width: 98vw;
    padding: 5px 12px 12px 12px;
  }
}
.moments-title {
  font-family: 'StayWonderful', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive;
  font-size: 2.75rem;
  font-weight: 300;
  color: #312d30;
  margin-bottom: 18px;
  margin-top: 0;
  text-align: center;
}
/* Hamburger menu only visible on mobile, buttons only on desktop */
.top-bar {
  position: static;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 0; /* Remove negative margin */
  margin-bottom: 0;
  margin-left: 0px;
  z-index: 100;
}
.top-bar-buttons {
  display: flex;
  gap: 12px;
}
.hamburger-menu-container {
  display: none;
}
@media (max-width: 800px) {
  .top-bar-buttons {
    display: none;
  }
  .hamburger-menu-container {
    display: flex;
    position: relative;
    align-items: center;
  }
}
.hamburger-btn {
  background: #e0e0e0;
  border: none;
  border-radius: 6px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: background 0.2s;
  margin: 0 20px 0 0;
  padding: 0;
}
.hamburger-btn:hover {
  background: #cccccc;
}
.hamburger-icon {
  display: block;
  width: 28px;
  height: 3px;
  background: #7da47e;
  border-radius: 2px;
  position: relative;
}
.hamburger-icon::before,
.hamburger-icon::after {
  content: '';
  display: block;
  width: 28px;
  height: 3px;
  background: #7da47e;
  border-radius: 2px;
  position: absolute;
  left: 0;
  transition: 0.2s;
}
.hamburger-icon::before {
  top: -9px;
}
.hamburger-icon::after {
  top: 9px;
}
.dropdown-menu {
  position: absolute;
  top: 56px;
  right: 0;
  background: #f5f5f5;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  min-width: 160px;
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  z-index: 2000;
}
.dropdown-item {
  background: none;
  border: none;
  color: #312d30;
  font-size: 1.13rem;
  font-weight: bold;
  text-align: left;
  padding: 12px 24px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  width: 100%;
  text-decoration: none;
}
.dropdown-item:hover {
  background: #f5eee6;
  color: #388E3C;
}
.top-logout-button, .top-account-button {
  background: #7da47e;
  color: #fff;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 1.13rem;
  font-weight: bold;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: background 0.2s, box-shadow 0.2s;
  border: none;
  cursor: pointer;
  display: inline-block;
  position: static;
  margin-right: 0;
  height: 40px;
}
.top-logout-button:hover, .top-account-button:hover {
  background: #388E3C;
}
/* Remove absolute positioning from .top-logout-link */
.top-logout-button {
  position: static;
  top: unset;
  right: unset;
  margin: 0 0 0 0;
}
.manage-account-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 5px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.97rem;
  font-weight: bold;
  margin-left: 8px;
  transition: background 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  display: flex;
  align-items: center;
  height: 32px;
}
.manage-account-btn:hover {
  background: #388E3C;
}
.add-message-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 32px;
  position: relative;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.message-input-error {
  box-shadow: 0 0 0 2px #c0392b;
  background-color: rgba(192, 57, 43, 0.08);
  border-radius: 6px;
  animation: message-shake 0.4s;
}
@keyframes message-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4px); }
  40%, 80% { transform: translateX(4px); }
}
.message-label {
  font-family: 'LemonCupcakeBold', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive;
  font-size: 2.25rem;
  color: #312d30;
  font-weight: 300;
}
input, select, textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px; /* Prevent mobile zoom */
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  width: 100%;
  box-sizing: border-box;
}
textarea {
  min-height: 80px;
  resize: vertical;
}
.char-count {
  font-size: 0.95rem;
  color: #888;
  margin-top: 2px;
  text-align: right;
}
.char-count.limit-reached {
  color: #c0392b;
  font-weight: bold;
}
button[type="submit"], .edit-btn, .delete-btn, .save-btn, .cancel-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 8px 22px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: bold;
  margin-right: 8px;
  margin-top: 8px;
  transition: background 0.2s;
}
button[type="submit"] {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 7px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  margin-right: 0;
  margin-top: 0;
  transition: background 0.2s;
  align-self: flex-end;
  min-width: 110px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
button[type="submit"]:disabled, .save-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}
.edit-btn, .delete-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 7px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.01rem;
  font-weight: bold;
  margin-right: 6px;
  margin-top: 4px;
  transition: background 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.edit-btn:hover {
  background: #388E3C;
}
.delete-btn {
  background: #d97b63; /* softer terra cotta red */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
}
.delete-btn:hover {
  background: #b85c3b; /* deeper terra cotta for hover */
}
.save-btn {
  background: #7da47e;
}
.save-btn:hover {
  background: #388E3C;
}
.cancel-btn {
  background: #888;
}
.cancel-btn:hover {
  background: #555;
}
.messages-list {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.message-item {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 7px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.02);
  margin: 0;
  transition: all 0.3s ease;
}
.message-item:hover {
  border-color: #7da47e;
  box-shadow: 0 2px 8px rgba(125, 164, 126, 0.1);
}
.message-item:after {
  display: none;
}
.message-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px;
  border-radius: 6px;
  position: relative;
}
.message-row:hover {
  background-color: #e8f5e8;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(125, 164, 126, 0.15);
}
.message-row:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(125, 164, 126, 0.2);
}
.message-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  min-width: 120px;
}

/* Hide action buttons on all devices since moments are now clickable */
.desktop-only {
  display: none !important;
}

/* Edit actions layout */
.edit-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
  justify-content: flex-end; /* Right justify on desktop */
}

/* Fix button heights on all screen sizes */
.edit-actions .save-btn, 
.edit-actions .cancel-btn, 
.edit-actions .mobile-delete {
  padding: 8px 22px !important;
  font-size: 1.05rem !important;
  font-weight: bold !important;
  line-height: 1 !important;
  height: 40px !important;
  box-sizing: border-box !important;
  border: none !important;
  margin: 0 !important;
  vertical-align: baseline !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.edit-actions .mobile-delete {
  background: #d97b63 !important;
}

.edit-actions .mobile-delete:hover {
  background: #b85c3b !important;
}

@media (max-width: 800px) {
  .edit-actions {
    justify-content: space-between; /* Override to space-between on mobile */
  }
  
  .save-btn, .cancel-btn {
    flex: 1;
    min-width: 0;
  }
  
  .mobile-delete {
    flex: 0 0 auto;
    margin-left: 8px !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-right: 0 !important;
  }
}

.message-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.message-text {
  font-family: 'Quicksand', 'Segoe UI', 'Arial', sans-serif;
  font-weight: 300;
  font-size: 1.05rem;
  color: #312d30;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.7;
  background: none;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  display: block;
  margin-bottom: 4px;
  word-break: break-word;
  white-space: pre-wrap;
}

.message-date {
  font-family: 'Quicksand', 'Segoe UI', 'Arial', sans-serif;
  font-size: 0.75rem;
  color: #888;
  font-weight: 400;
  margin-top: 2px;
  opacity: 0.8;
}

@media (max-width: 800px) {
  .message-text {
    font-size: 1rem;
    line-height: 1.6;
  }
  
  .message-date {
    font-size: 0.7rem;
  }
}
.your-messages-section-title{
  font-family: 'StayWonderful', 'Pacifico', 'Segoe UI', 'Georgia', serif;
  font-size: 1.95rem;
  font-weight: 300;
  color: #312d30;
  margin-bottom: 8px;
}
.edit-section textarea {
  width: 100%;
}

.edit-date {
  margin-top: 4px;
  margin-bottom: 6px;
  text-align: left;
}

.no-messages {
  margin-top: 24px;
  color: #888;
  text-align: center;
  font-size: 1.08rem;
}
.dialog-form-group-row {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.dialog-form-group label {
  font-weight: bold;
  color: #312d30;
  font-size: 1rem;
  text-align: left;
}
.dialog-form-group input {
  padding: 7px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px; /* Prevent mobile zoom */
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}
.dialog-phone-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.dialog-phone-row input {
  flex: 1 1 0;
}
.dialog-validate-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 7px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background 0.2s;
  margin-left: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.dialog-validate-btn:hover {
  background: #388E3C;
}
.loading-messages {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  color: #666;
  font-style: italic;
}

/* Loading state for buttons */
.loading {
  pointer-events: none;
  opacity: 0.8;
}

/* Attempts exceeded dialog specific styling */
.attempts-exceeded-dialog {
  max-width: 450px;
}

.attempts-exceeded-content {
  text-align: center;
  margin-bottom: 20px;
}

.attempts-exceeded-message {
  color: #dc2626;
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
}

.attempts-exceeded-suggestion {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.attempts-exceeded-actions {
  justify-content: center;
}

.attempts-exceeded-actions .dialog-save-btn {
  background: #7da47e;
}

.attempts-exceeded-actions .dialog-save-btn:hover {
  background: #388E3C;
}
.button {
  height: 35px; /* Set a consistent height for both buttons */
  min-width: 70px; /* Optional: ensure buttons have a minimum width */
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.add-insight-email-note {
  text-align: center;
  font-size: smaller;
}
</style>
